import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getCorrectnessV2CustomerView,
  submitCorrectnessV2MoneyConfirmation,
  submitCorrectnessV2FindingConfirmation,
  getCorrectnessV2Translation,
} from '../../lib/api/perizia';

// Single source of truth for the sanitized Correctness V2 customer report.
//
// Both AnalysisResult (page shell / loading state) and CustomerReportView
// (to render) read the SAME hook instance so the sanitized endpoint is fetched
// once and the availability answer never diverges between the page and the
// rendered report.
//
// A generic network failure or a 404 (feature disabled / no report) is treated
// as "unavailable": `available` is false and CustomerReportView renders the
// matching safe state (never a blank page).
//
// SELECTED LOT (URL-persisted): the page owns the `?lot=` search param and
// passes it in as `selectedLotId` together with an `onSelectLot` writer, so
// refresh / back / forward / deep links preserve the selected lot. When no
// controller is supplied (standalone use / unit tests) the hook falls back to
// plain internal state, exactly as before.
//
// SIDE-EFFECT-FREE READS: the customer-view GET only *reports* state — it
// never creates jobs (generation happens exclusively through the explicit
// POST /lots/{lot_id}/generate action). Selecting/opening a lot therefore
// never auto-generates, and polling while `preparing` is safe: it can never
// respawn work server-side.
//
// When the backend reports `preparing: true` (a V2 job is running for this
// analysis — e.g. auto-started on upload or explicitly started from the lot
// workspace), the hook silently re-polls the sanitized endpoint until the
// report appears or a terminal outcome makes `preparing` false. Silent polls
// never toggle `loading`, so the page never flashes back to a placeholder
// while waiting.

const PREPARING_POLL_MS = 8000;
const PREPARING_MAX_POLLS = 225; // ~30 minutes of background polling

const isCanceledError = (error) => (
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'CanceledError' ||
  error?.message === 'canceled'
);

export const useCorrectnessV2CustomerView = (
  analysisId,
  { enabled = true, selectedLotId: controlledLotId, onSelectLot } = {},
) => {
  const active = Boolean(enabled && analysisId);
  const controlled = typeof onSelectLot === 'function';
  const [loading, setLoading] = useState(active);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [internalLotId, setInternalLotId] = useState(null);
  const selectedLotId = controlled ? (controlledLotId || null) : internalLotId;
  // Remembered lot-selection report: when a customer picks a lot whose report
  // does not exist yet, we keep the selector data so they can navigate back
  // instead of hitting a dead end.
  const [lotSelectionReport, setLotSelectionReport] = useState(null);
  // Money-confirmation submission state (human-in-the-loop disambiguation).
  const [confirmingMoney, setConfirmingMoney] = useState(false);
  const [moneyConfirmError, setMoneyConfirmError] = useState('');
  // Focused finding-confirmation submission state (decision-model findings).
  const [confirmingFinding, setConfirmingFinding] = useState(false);
  const [findingConfirmError, setFindingConfirmError] = useState('');
  // Lazily-fetched English translations (progressive enhancement). Never blocks
  // or alters the Italian report; a failure just leaves this empty.
  const [translations, setTranslations] = useState(null);
  const translatedJobRef = useRef(null);
  const mountedRef = useRef(false);
  const pollCountRef = useRef(0);
  // Bumped by reload() to re-run the initial fetch (customer retry action).
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback((lotId, signal, { silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }
    return getCorrectnessV2CustomerView(analysisId, { selected_lot_id: lotId || undefined }, { signal })
      .then((response) => {
        if (!mountedRef.current) return;
        const data = response.data;
        setPayload(data);
        if (data?.available && data?.report?.report_status === 'LOT_SELECTION_REQUIRED') {
          setLotSelectionReport(data.report);
        }
        if (silent) setError('');
      })
      .catch((err) => {
        if (isCanceledError(err) || !mountedRef.current) return;
        if (err?.response?.status === 404) {
          // Feature disabled or no report for this analysis -> unavailable, not error.
          setPayload({ available: false, reason_code: 'CORRECTNESS_V2_DISABLED' });
          return;
        }
        if (silent) return; // Keep the last good payload on background poll errors.
        // Any other failure is a safe "unavailable": CustomerReportView shows
        // its service-unavailable state with a retry, never a blank page.
        setError('Impossibile caricare il report cliente.');
        setPayload({ available: false, reason_code: 'ERROR' });
      })
      .finally(() => {
        if (mountedRef.current && !silent) setLoading(false);
      });
  }, [analysisId]);

  useEffect(() => {
    if (!active) {
      setLoading(false);
      return undefined;
    }
    pollCountRef.current = 0;
    const controller = new AbortController();
    load(selectedLotId, controller.signal);
    return () => controller.abort();
  }, [active, load, selectedLotId, reloadTick]);

  const preparing = Boolean(payload && !payload.available && payload.preparing);

  // Background poll while the backend is preparing the report for us. The GET
  // is side-effect-free (it can never start a job), so this poll only waits.
  useEffect(() => {
    if (!active || !preparing) return undefined;
    if (pollCountRef.current >= PREPARING_MAX_POLLS) return undefined;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => {
      pollCountRef.current += 1;
      load(selectedLotId, controller.signal, { silent: true });
    }, PREPARING_POLL_MS);
    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [active, preparing, load, selectedLotId, payload]);

  const report = payload && payload.available ? (payload.report || null) : null;
  const available = Boolean(payload && payload.available && payload.report);
  const isLotSelection = report?.report_status === 'LOT_SELECTION_REQUIRED';
  const isMoneyConfirmation = report?.report_status === 'MONEY_CONFIRMATION_REQUIRED';

  // Progressive-enhancement English: fetched ONCE per job AFTER the Italian
  // report is available, only when the backend signals report-clarity is on
  // (decision_model.clarity_enabled). Side-effect-free, quota-exempt, fail-soft:
  // any error simply leaves the Italian report as-is.
  const jobId = report?.job_id || null;
  const clarityOn = Boolean(report?.decision_model?.clarity_enabled);
  useEffect(() => {
    if (!active || !available || !jobId || !clarityOn) return undefined;
    if (translatedJobRef.current === jobId) return undefined;
    translatedJobRef.current = jobId;
    const controller = new AbortController();
    getCorrectnessV2Translation(analysisId, jobId, { signal: controller.signal })
      .then((response) => {
        if (!mountedRef.current) return;
        const data = response.data;
        if (data?.available && Array.isArray(data.translations)) {
          setTranslations(data.translations);
        }
      })
      .catch(() => { /* fail-soft: Italian report is unaffected */ });
    return () => controller.abort();
  }, [active, available, analysisId, jobId, clarityOn]);

  // Submit the customer's answers {ambiguity_id: option_id}; on success the
  // server returns the finalized report, which we swap in directly.
  const submitMoneyConfirmation = useCallback((answers) => {
    const jobId = report?.job_id;
    if (!jobId) return Promise.resolve(false);
    setConfirmingMoney(true);
    setMoneyConfirmError('');
    return submitCorrectnessV2MoneyConfirmation(analysisId, jobId, answers)
      .then((response) => {
        if (!mountedRef.current) return false;
        const data = response.data;
        if (data?.available && data?.report) {
          setPayload({ available: true, report: data.report });
          return true;
        }
        // Answers accepted but the report still is not customer-ready.
        setMoneyConfirmError('Le conferme non hanno risolto tutte le verifiche necessarie.');
        return false;
      })
      .catch((err) => {
        if (!mountedRef.current) return false;
        const detail = err?.response?.data?.detail;
        setMoneyConfirmError(
          detail?.reason_human || 'Impossibile inviare le conferme. Riprovare.'
        );
        return false;
      })
      .finally(() => {
        if (mountedRef.current) setConfirmingMoney(false);
      });
  }, [analysisId, report]);
  // Submit/update a focused confirmation on a decision-model finding. On success
  // the server returns the refreshed sanitized report, swapped in directly.
  // Returns true when the confirmation was saved. Zero jobs/credits/OpenAI.
  const submitFindingConfirmation = useCallback((findingId, optionId, note) => {
    const jobId = report?.job_id;
    if (!jobId || !findingId || !optionId) return Promise.resolve(false);
    setConfirmingFinding(true);
    setFindingConfirmError('');
    return submitCorrectnessV2FindingConfirmation(analysisId, jobId, findingId, optionId, note)
      .then((response) => {
        if (!mountedRef.current) return false;
        const data = response.data;
        if (data?.available && data?.report) {
          setPayload({ available: true, report: data.report });
          return true;
        }
        setFindingConfirmError('Impossibile salvare la conferma.');
        return false;
      })
      .catch((err) => {
        if (!mountedRef.current) return false;
        const detail = err?.response?.data?.detail;
        setFindingConfirmError(detail?.reason_human || 'Impossibile salvare la conferma. Riprovare.');
        return false;
      })
      .finally(() => {
        if (mountedRef.current) setConfirmingFinding(false);
      });
  }, [analysisId, report]);

  // A lot was selected but its report is not available (yet): keep the user in
  // the customer surface (message + back to lots) instead of a dead end.
  const lotUnavailable = Boolean(
    selectedLotId && payload && !payload.available && lotSelectionReport
  );

  // Selecting a lot ONLY changes which report is fetched/displayed (URL param
  // in controlled mode). It never triggers generation.
  const selectLot = useCallback((lotId) => {
    if (controlled) onSelectLot(lotId || null);
    else setInternalLotId(lotId || null);
  }, [controlled, onSelectLot]);
  const backToLots = useCallback(() => selectLot(null), [selectLot]);
  const reload = useCallback(() => {
    setError('');
    setReloadTick((tick) => tick + 1);
  }, []);

  return {
    loading,
    error,
    reload,
    payload,
    report,
    translations,
    available,
    preparing,
    isLotSelection,
    isMoneyConfirmation,
    submitMoneyConfirmation,
    confirmingMoney,
    moneyConfirmError,
    submitFindingConfirmation,
    confirmingFinding,
    findingConfirmError,
    lotUnavailable,
    lotSelectionReport,
    selectedLotId,
    selectLot,
    backToLots,
  };
};

export default useCorrectnessV2CustomerView;
